"use client";

import { c as _c } from "react/compiler-runtime";
import { AdditionalMedia, MemesSubmissionAdditionalInfoKey } from "@/components/waves/memes/submission/types/OperationalData";
import { FallbackImage } from "@/components/common/FallbackImage";
import { resolveIpfsUrlSync } from "@/components/ipfs/IPFSContext";
import { getFileInfoFromUrl } from "@/helpers/file.helpers";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useMemo } from "react";
const MAX_MEDIA = 4;
const VIDEO_EXTENSIONS = new Set(["mp4", "mov", "m4v", "webm", "ogv"]);
const emptyAdditionalMedia: AdditionalMedia = {
  artist_profile_media: [],
  artwork_commentary_media: [],
  preview_image: "",
  promo_video: ""
};
const parseAdditionalMedia = (rawValue?: string): AdditionalMedia => {
  if (!rawValue) {
    return emptyAdditionalMedia;
  }
  try {
    const parsed = JSON.parse(rawValue) as Partial<AdditionalMedia> | null;
    if (!parsed || typeof parsed !== "object") {
      return emptyAdditionalMedia;
    }
    return {
      artist_profile_media: Array.isArray(parsed.artist_profile_media) ? parsed.artist_profile_media.filter(Boolean) : [],
      artwork_commentary_media: Array.isArray(parsed.artwork_commentary_media) ? parsed.artwork_commentary_media.filter(Boolean) : [],
      preview_image: typeof parsed.preview_image === "string" ? parsed.preview_image : "",
      promo_video: typeof parsed.promo_video === "string" ? parsed.promo_video : ""
    };
  } catch {
    return emptyAdditionalMedia;
  }
};
const isVideoUrl = (url: string) => {
  const fileInfo = getFileInfoFromUrl(url);
  if (!fileInfo?.extension) {
    return false;
  }
  return VIDEO_EXTENSIONS.has(fileInfo.extension.toLowerCase());
};
interface WaveDropAdditionalInfoProps {
  readonly drop: ExtendedDrop;
}
export const WaveDropAdditionalInfo = t0 => {
  const $ = _c(41);
  const {
    drop
  } = t0;
  let aboutArtistValue;
  let commentaryValue;
  let previewImageValue;
  let promoVideoValue;
  let t1;
  if ($[0] !== drop.metadata) {
    const metadata = drop.metadata ?? [];
    const getMetadataValue = key => metadata.find(item => item.data_key === key)?.data_value?.trim() ?? "";
    let t2;
    if ($[6] !== getMetadataValue) {
      t2 = getMetadataValue(MemesSubmissionAdditionalInfoKey.COMMENTARY);
      $[6] = getMetadataValue;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    commentaryValue = t2;
    let t3;
    if ($[8] !== getMetadataValue) {
      t3 = getMetadataValue(MemesSubmissionAdditionalInfoKey.ABOUT_ARTIST);
      $[8] = getMetadataValue;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    aboutArtistValue = t3;
    const additionalMediaEntry = metadata.find(_temp);
    const additionalMedia = parseAdditionalMedia(additionalMediaEntry?.data_value);
    previewImageValue = additionalMedia.preview_image ? resolveIpfsUrlSync(additionalMedia.preview_image) : "";
    promoVideoValue = additionalMedia.promo_video ? resolveIpfsUrlSync(additionalMedia.promo_video) : "";
    t1 = additionalMedia.artwork_commentary_media.filter(_temp2).map(_temp3).filter(_temp4).map(_temp5);
    $[0] = drop.metadata;
    $[1] = aboutArtistValue;
    $[2] = commentaryValue;
    $[3] = previewImageValue;
    $[4] = promoVideoValue;
    $[5] = t1;
  } else {
    aboutArtistValue = $[1];
    commentaryValue = $[2];
    previewImageValue = $[3];
    promoVideoValue = $[4];
    t1 = $[5];
  }
  const mediaItemsValue = t1;
  let t2;
  if ($[10] !== aboutArtistValue || $[11] !== commentaryValue || $[12] !== mediaItemsValue || $[13] !== previewImageValue || $[14] !== promoVideoValue) {
    t2 = {
      commentary: commentaryValue,
      aboutArtist: aboutArtistValue,
      previewImage: previewImageValue,
      promoVideo: promoVideoValue,
      mediaItems: mediaItemsValue
    };
    $[10] = aboutArtistValue;
    $[11] = commentaryValue;
    $[12] = mediaItemsValue;
    $[13] = previewImageValue;
    $[14] = promoVideoValue;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  const {
    commentary,
    aboutArtist,
    previewImage,
    promoVideo,
    mediaItems
  } = t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[16] !== aboutArtist || $[17] !== commentary || $[18] !== mediaItems || $[19] !== previewImage || $[20] !== promoVideo) {
    t7 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const displayedMedia = mediaItems.slice(0, MAX_MEDIA);
      const hasContent = previewImage || promoVideo || displayedMedia.length > 0 || aboutArtist || commentary;
      if (!hasContent) {
        t7 = null;
        break bb0;
      }
      t3 = "tw-space-y-8";
      if ($[26] !== previewImage) {
        t4 = previewImage && <div className="tw-space-y-2"><h3 className="tw-text-base tw-font-semibold tw-text-iron-100">Preview Image</h3><div className="tw-flex tw-justify-center"><div className="tw-relative tw-aspect-[4/3] tw-w-full tw-max-w-2xl tw-overflow-hidden tw-bg-white/[0.02]"><FallbackImage primarySrc={getScaledImageUri(previewImage, ImageScale.AUTOx600)} fallbackSrc={previewImage} alt="Preview image" fill={true} sizes="(min-width: 768px) 400px, 100vw" className="tw-object-contain" /></div></div></div>;
        $[26] = previewImage;
        $[27] = t4;
      } else {
        t4 = $[27];
      }
      if ($[28] !== promoVideo) {
        t5 = promoVideo && <div className="tw-space-y-2"><h3 className="tw-text-base tw-font-semibold tw-text-iron-100">Promo Video</h3><div className="tw-flex tw-justify-center"><div className="tw-relative tw-aspect-video tw-w-full tw-max-w-2xl tw-overflow-hidden tw-bg-white/[0.02]"><video src={promoVideo} className="tw-h-full tw-w-full tw-object-contain" controls={true} preload="metadata"><track kind="captions" src="data:text/vtt,WEBVTT" srcLang="en" label="Captions" /></video></div></div></div>;
        $[28] = promoVideo;
        $[29] = t5;
      } else {
        t5 = $[29];
      }
      t6 = displayedMedia.length > 0 && <div className="tw-space-y-2"><h3 className="tw-text-base tw-font-semibold tw-text-iron-100">Additional Media</h3><div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2 md:tw-gap-4">{displayedMedia.map(_temp6)}</div></div>;
    }
    $[16] = aboutArtist;
    $[17] = commentary;
    $[18] = mediaItems;
    $[19] = previewImage;
    $[20] = promoVideo;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
  } else {
    t3 = $[21];
    t4 = $[22];
    t5 = $[23];
    t6 = $[24];
    t7 = $[25];
  }
  if (t7 !== Symbol.for("react.early_return_sentinel")) {
    return t7;
  }
  let t8;
  if ($[30] !== aboutArtist) {
    t8 = aboutArtist && <div className="tw-space-y-2"><h3 className="tw-text-base tw-font-semibold tw-text-iron-100">About the Artist</h3><p className="tw-mb-0 tw-whitespace-pre-wrap tw-text-sm tw-leading-relaxed tw-text-iron-400">{aboutArtist}</p></div>;
    $[30] = aboutArtist;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== commentary) {
    t9 = commentary && <div className="tw-space-y-2"><h3 className="tw-text-base tw-font-semibold tw-text-iron-100">Artwork Commentary</h3><p className="tw-mb-0 tw-whitespace-pre-wrap tw-text-sm tw-leading-relaxed tw-text-iron-400">{commentary}</p></div>;
    $[32] = commentary;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== t3 || $[35] !== t4 || $[36] !== t5 || $[37] !== t6 || $[38] !== t8 || $[39] !== t9) {
    t10 = <section className={t3}>{t4}{t5}{t6}{t8}{t9}</section>;
    $[34] = t3;
    $[35] = t4;
    $[36] = t5;
    $[37] = t6;
    $[38] = t8;
    $[39] = t9;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  return t10;
};
function _temp(item_0) {
  return item_0.data_key === MemesSubmissionAdditionalInfoKey.ADDITIONAL_MEDIA;
}
function _temp2(url) {
  return typeof url === "string" && url.trim().length > 0;
}
function _temp3(url_0) {
  return resolveIpfsUrlSync(url_0);
}
function _temp4(url_1) {
  return url_1.length > 0;
}
function _temp5(url_2) {
  const isVideo = isVideoUrl(url_2);
  const displayUrl = isVideo ? url_2 : getScaledImageUri(url_2, ImageScale.AUTOx600);
  return {
    url: url_2,
    displayUrl,
    isVideo
  };
}
function _temp6(item_1, index) {
  return <div key={`${item_1.url}-${index}`} className={`tw-relative tw-overflow-hidden tw-bg-white/[0.02] ${item_1.isVideo ? "tw-aspect-video sm:tw-col-span-2" : "tw-aspect-[4/3]"}`}>{item_1.isVideo ? <video src={item_1.url} className="tw-h-full tw-w-full tw-object-cover" controls={true} preload="metadata"><track kind="captions" src="data:text/vtt,WEBVTT" srcLang="en" label="Captions" /></video> : <FallbackImage primarySrc={item_1.displayUrl} fallbackSrc={item_1.url} alt={`Additional media ${index + 1}`} fill={true} sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw" className="tw-object-contain" />}</div>;
}